import type { IAttentionGeoNode, IAttentionOwnerAssignment, IAttentionPartner } from './types'

/** The bucket for partners no PBM is named for, kept beside the real ones so no partner is lost. */
export const UNASSIGNED_PBM_KEY = 'unassigned-pbm'

/** Raw fallback label of that bucket, same caveat as the geo tree's `Unmapped`. */
export const UNASSIGNED_PBM_LABEL = 'No PBM assigned'

export const UNASSIGNED_MANAGER_KEY = 'unassigned-manager'

export const UNASSIGNED_MANAGER_LABEL = 'No manager assigned'

interface IBuildAttentionOwnerNodesParams {
  partners: IAttentionPartner[]
  assignments: IAttentionOwnerAssignment[]
  /** The bucket partners nobody owns are listed under. */
  unassigned: { itemId: string; name: string }
}

/**
 * Groups partners by the people that own them — PBMs or managers. One flat level: an owner has no
 * level under it the way a market has clusters and countries.
 *
 * A partner owned by two people stands under both: the assignment is per offering, and a reader
 * looking at one owner's list expects every partner that owner answers for.
 */
export const buildAttentionOwnerNodes = ({
  partners,
  assignments,
  unassigned,
}: IBuildAttentionOwnerNodesParams): IAttentionGeoNode[] => {
  const ownersByPartner = new Map<string, IAttentionOwnerAssignment[]>()

  assignments.forEach((assignment) => {
    const owners = ownersByPartner.get(assignment.partnerId)

    if (owners) {
      owners.push(assignment)
      return
    }

    ownersByPartner.set(assignment.partnerId, [assignment])
  })

  const nodes = new Map<string, IAttentionGeoNode>()
  const partnerIdsByNode = new Map<string, Set<string>>()

  const put = (itemId: string, name: string, partnerId: string) => {
    const taken = partnerIdsByNode.get(itemId)

    if (taken) {
      /* One owner of a partner for two offerings is one row of that partner, not two. */
      if (!taken.has(partnerId)) {
        taken.add(partnerId)
        nodes.get(itemId)?.partnerIds.push(partnerId)
      }

      return
    }

    partnerIdsByNode.set(itemId, new Set([partnerId]))
    nodes.set(itemId, { itemId, name, depth: 0, partnerIds: [partnerId], children: [] })
  }

  partners.forEach(({ partnerId }) => {
    const owners = ownersByPartner.get(partnerId) ?? []

    if (owners.length === 0) {
      put(unassigned.itemId, unassigned.name, partnerId)
      return
    }

    owners.forEach(({ ownerId, ownerName }) => put(ownerId, ownerName, partnerId))
  })

  /* Named owners read alphabetically; the bucket of partners nobody owns closes the list. */
  return [...nodes.values()].sort((left, right) => {
    if (left.itemId === unassigned.itemId) {
      return 1
    }

    if (right.itemId === unassigned.itemId) {
      return -1
    }

    return left.name.localeCompare(right.name)
  })
}
